import { Text } from '@/components/ui/Text';
import {
	type CancellationPreferenceTypes,
	freeCancellationDetails,
} from '@/lib/cancellationPolicy';
import { formatDate } from '@/lib/dates';
import { useCurrentLocale, useI18n } from '@/locales';

/** The three parts of the policy sentence, so callers can emphasise the middle. */
export const useCancellationPolicyText = (
	policy: CancellationPreferenceTypes | string,
	checkIn: string,
) => {
	const t = useI18n();
	const locale = useCurrentLocale();
	const details = freeCancellationDetails(
		policy as CancellationPreferenceTypes,
		checkIn,
	);

	if (!details) return null;

	const { date, isRefundable, refundPercentage } = details;
	return {
		lead: isRefundable
			? t('booking.cancellation.freeUntil')
			: t('booking.cancellation.nonRefundable'),
		highlight: isRefundable
			? formatDate(date.toString(), locale)
			: t('booking.cancellation.nonRefundableHighlight'),
		footer:
			!isRefundable || refundPercentage === 0
				? t('booking.cancellation.chargedInFull')
				: t('booking.cancellation.noRefundAfterWindow'),
	};
};

interface CancellationPolicyProps {
	policy: CancellationPreferenceTypes | string;
	checkIn: string;
}

/**
 * The cancellation terms for the chosen check-in, in full. Renders nothing
 * without a check-in date.
 */
export const CancellationPolicy = ({
	policy,
	checkIn,
}: CancellationPolicyProps) => {
	const text = useCancellationPolicyText(policy, checkIn);

	if (!text) return null;

	return (
		<Text variant="bodySmall">
			{text.lead} <strong>{text.highlight}</strong>. {text.footer}
		</Text>
	);
};
